import React, { useEffect, useMemo, useState } from "react";
import { History, Search, X, RefreshCcw } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { HistoryList } from "./ProcessHistoryComponents";

// Same label mapping used by the entry card (kept in sync).
const ACTION_LABELS = {
  process_created:               "Processo criado",
  process_updated:               "Processo atualizado",
  process_deleted:               "Processo excluído",
  process_client_updated:        "Dados do cliente atualizados",
  process_responsible_changed:   "Responsável alterado",
  process_priority_changed:      "Prioridade alterada",
  process_status_changed:        "Status alterado",
  process_workflow_changed:      "Workflow atualizado",
  process_progress_updated:      "Progresso atualizado",
  process_notes_updated:         "Notas atualizadas",
  process_portal_status_changed: "Acesso do portal alterado",
  timeline_created:              "Evento de timeline criado",
  timeline_deleted:              "Evento de timeline removido",
  document_uploaded:             "Documento anexado",
  document_deleted:              "Documento excluído",
  whatsapp_notification_sent:    "Notificação WhatsApp enviada",
  whatsapp_notification_failed:  "Falha ao enviar WhatsApp",
};

export default function ProcessesHistoryPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [processes, setProcesses] = useState([]);
  const [actors, setActors] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [q, setQ] = useState("");
  const [processId, setProcessId] = useState("");
  const [actorId, setActorId] = useState("");
  const [action, setAction] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Load processes list for the picker once
  useEffect(() => {
    api.get("/processes?page_size=500")
      .then(({ data }) => setProcesses(data.items || []))
      .catch(() => {});
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (processId) params.set("process_id", processId);
    if (actorId) params.set("actor_id", actorId);
    if (action) params.set("action", action);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    params.set("page_size", "200");
    try {
      const { data } = await api.get(`/process-history?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setActors(data.actors || []);
      setActions(data.actions || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setItems([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchHistory, q ? 220 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, processId, actorId, action, dateFrom, dateTo]);

  const hasFilters = q || processId || actorId || action || dateFrom || dateTo;
  const clearAll = () => { setQ(""); setProcessId(""); setActorId(""); setAction(""); setDateFrom(""); setDateTo(""); };

  const actionOptions = useMemo(
    () => actions.map((k) => ({ value: k, label: ACTION_LABELS[k] || k })),
    [actions]
  );

  return (
    <div data-testid="processes-page-history" className="space-y-5">
      {/* Header */}
      <div className="rounded-2xl border border-border bg-white p-5 flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-primary/10 border border-primary/30 grid place-items-center">
          <History className="h-5 w-5 text-primary" strokeWidth={2.2} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl md:text-2xl font-display font-bold tracking-tight text-foreground">Histórico</h1>
          <p className="text-sm text-muted-foreground">
            Auditoria imutável de todas as ações realizadas nos processos.
            Somente leitura — os registros não podem ser editados nem removidos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs" data-testid="history-count">
            {loading ? "…" : `${total} ${total === 1 ? "registro" : "registros"}`}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchHistory}
            data-testid="history-refresh"
          >
            <RefreshCcw className="h-4 w-4 mr-1" /> Atualizar
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-border bg-white p-4 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs">Buscar</Label>
          <div className="relative mt-1">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="history-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Usuário, processo, cliente, ação…"
              className="pl-9"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Processo</Label>
          <select
            data-testid="history-filter-process"
            value={processId}
            onChange={(e) => setProcessId(e.target.value)}
            className="mt-1 block w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="">Todos</option>
            {processes.map((p) => {
              const lbl = `${p.process_number} · ${p.client_name || p.service_name || "—"}`;
              return <option key={p.id} value={p.id}>{lbl}</option>;
            })}
          </select>
        </div>
        <div>
          <Label className="text-xs">Usuário</Label>
          <select
            data-testid="history-filter-actor"
            value={actorId}
            onChange={(e) => setActorId(e.target.value)}
            className="mt-1 block w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="">Todos</option>
            {actors.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs">Ação</Label>
          <select
            data-testid="history-filter-action"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="mt-1 block w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="">Todas</option>
            {actionOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2 md:col-span-3 xl:col-span-6">
          <div>
            <Label className="text-xs">De</Label>
            <Input
              data-testid="history-filter-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Até</Label>
            <Input
              data-testid="history-filter-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="mt-1"
            />
          </div>
        </div>
        {hasFilters && (
          <div className="md:col-span-3 xl:col-span-6 flex justify-end">
            <Button variant="ghost" size="sm" onClick={clearAll} data-testid="history-clear">
              <X className="h-4 w-4 mr-1" /> Limpar filtros
            </Button>
          </div>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div
          data-testid="history-loading"
          className="rounded-2xl border border-dashed border-border bg-white px-10 py-14 text-center text-sm text-muted-foreground"
        >
          Carregando histórico…
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-white p-4">
          <HistoryList
            items={items}
            showProcess
            emptyLabel={hasFilters
              ? "Nenhum registro encontrado com esses filtros."
              : "Ainda não há registros no histórico."}
          />
        </div>
      )}
    </div>
  );
}
